import React from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import { AppSidebar } from '../components/AppSidebar';
import { AppToastViewport } from '../components/AppToastViewport';
import { AppTopBar } from '../components/AppTopBar';
import { useCapabilities } from '../hooks/useCapabilities';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useToasts } from '../hooks/useToasts';
import { fetchMe } from '../lib/meClient';
import { APP_BTN_OUTLINE_CLASS } from '../ui/controlStyles';
import { fetchTaskLeadOptions, fetchTaskMembers } from '../features/aufgaben/api';
import { TaskCreatePanel } from '../features/aufgaben/components/TaskCreatePanel';
import { TaskDetailPanel } from '../features/aufgaben/components/TaskDetailPanel';
import { TaskList } from '../features/aufgaben/components/TaskList';
import { TaskTeamOverview } from '../features/aufgaben/components/TaskTeamOverview';
import { NewTaskButton, TaskViewChips, TaskViewNav, TaskViewSelect } from '../features/aufgaben/components/TaskViewNav';
import { isTypingTarget } from '../features/aufgaben/components/taskUi';
import {
  TASK_VIEWS,
  VIEW_SLUGS,
  canEditTaskWith,
  countLabel,
  getTaskView,
  groupDoneTasks,
  groupTasks,
  localYmd,
  matchesTaskQuery,
  viewFromSlug,
  type TaskGroupBy,
  type TaskViewId,
} from '../features/aufgaben/taskViews';
import type { Task, TaskLeadOption, TaskMember } from '../features/aufgaben/types';
import { useTasks } from '../features/aufgaben/useTasks';

/** Ab hier steht die linke Ansichten-Spalte neben der Liste. */
const NAV_MIN_WIDTH = 1100;
/** Ab hier steht das Detail-Panel neben der Liste statt darüber. */
const INLINE_PANEL_MIN_WIDTH = 1400;
/** Darunter: Handy-Layout (Chips, Vollbild-Panel) — entspricht Tailwinds `md`. */
const MOBILE_MAX_WIDTH = 768;

function useWindowWidth(): number {
  const [w, setW] = React.useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  React.useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return w;
}

/**
 * Mobil füllt das Panel den Bildschirm — die Zurück-Geste des Browsers soll es schließen statt die Seite zu verlassen.
 * Beim Öffnen kommt ein History-Eintrag dazu; Schließen per Knopf nimmt ihn wieder weg.
 */
function usePanelHistoryEntry(open: boolean, enabled: boolean, onBack: () => void) {
  const navigate = useNavigate();
  const location = useLocation();
  const inHistory = (location.state as { todoPanel?: boolean } | null)?.todoPanel === true;
  const prev = React.useRef({ open, inHistory });
  const onBackRef = React.useRef(onBack);
  onBackRef.current = onBack;
  React.useEffect(() => {
    const was = prev.current;
    prev.current = { open, inHistory };
    if (!enabled) return;
    if (open && !was.open && !inHistory) navigate(location.pathname, { state: { todoPanel: true } });
    else if (open && was.inHistory && !inHistory) onBackRef.current();
    else if (!open && inHistory) navigate(-1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur auf Öffnen/Schließen und Zurück reagieren
  }, [open, inHistory, enabled]);
}

const GROUP_BY_STORAGE_KEY = 'todos.groupBy';

/** Gruppierung pro Ansicht merken — nur Komfort, darf fehlen (privates Fenster, gesperrter Speicher). */
function readGroupByPrefs(): Partial<Record<TaskViewId, TaskGroupBy>> {
  try {
    const raw = window.localStorage.getItem(GROUP_BY_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Partial<Record<TaskViewId, TaskGroupBy>>) : {};
  } catch {
    return {};
  }
}

function writeGroupByPrefs(prefs: Partial<Record<TaskViewId, TaskGroupBy>>) {
  try {
    window.localStorage.setItem(GROUP_BY_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignorieren */
  }
}

/** Datum für „heute" — springt um Mitternacht weiter, auch wenn die Seite offen bleibt. */
function useToday(): string {
  const [today, setToday] = React.useState(() => localYmd());
  React.useEffect(() => {
    const id = window.setInterval(() => setToday(localYmd()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return today;
}

export function AufgabenPage() {
  const { viewSlug } = useParams<{ viewSlug?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const instancePrefix = React.useMemo(() => location.pathname.match(/^\/i\/[^/]+/)?.[0] ?? '/i/default', [location.pathname]);

  const { can } = useCapabilities();
  // Eigene anlegen/bearbeiten (Zugang), alle ansehen, alle bearbeiten
  const canEdit = can('web.aufgaben.own');
  const canViewAll = can('web.aufgaben.view');
  const canEditAll = can('web.aufgaben.edit');
  const canOpenProjects = can('web.projekte.view');

  const [me, setMe] = React.useState<{ userId: string | null; instanceId: string | null; isAdmin: boolean }>({
    userId: null,
    instanceId: null,
    isAdmin: false,
  });
  React.useEffect(() => {
    let cancelled = false;
    fetchMe().then((res) => {
      if (cancelled || !res) return;
      setMe({
        userId: res.user?.id ?? null,
        instanceId: res.activeInstance?.id ?? null,
        isAdmin: res.role === 'owner' || res.role === 'admin',
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const { toasts, pushToast, dismissToast } = useToasts();
  const tasksApi = useTasks({ instanceId: me.instanceId, pushToast });
  const { tasks, loading } = tasksApi;

  const [members, setMembers] = React.useState<TaskMember[]>([]);
  React.useEffect(() => {
    fetchTaskMembers().then(setMembers, () => setMembers([]));
  }, []);
  const membersById = React.useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  const [leadOptions, setLeadOptions] = React.useState<TaskLeadOption[]>([]);
  React.useEffect(() => {
    if (!canEdit) return;
    fetchTaskLeadOptions().then(setLeadOptions, () => setLeadOptions([]));
  }, [canEdit]);

  const slugView = viewFromSlug(viewSlug);
  const showTeam = me.isAdmin && canViewAll;
  const view: TaskViewId | 'team' =
    (slugView === 'team' && !showTeam) || (slugView === 'all' && !canViewAll) ? 'mine' : slugView ?? 'mine';
  const canEditTask = React.useCallback(
    (t: Task) => canEditTaskWith({ userId: me.userId, own: canEdit, viewAll: canViewAll, editAll: canEditAll }, t),
    [canEdit, canEditAll, canViewAll, me.userId],
  );
  const selectView = React.useCallback(
    (next: TaskViewId | 'team') => navigate(`${instancePrefix}/todos/${VIEW_SLUGS[next]}`),
    [instancePrefix, navigate],
  );

  const [groupByPrefs, setGroupByPrefs] = React.useState(readGroupByPrefs);
  const [query, setQuery] = React.useState('');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  /** Anlege-Panel offen; Zähler erzwingt frisches Formular bei erneutem Öffnen. */
  const [creating, setCreating] = React.useState<number | null>(null);
  const selected = selectedId ? tasks.find((t) => t.id === selectedId) ?? null : null;

  // Direktlink ?task=<id> (Glocke, E-Mail, Projekt-Verlauf, KI): To-do öffnen, passende Ansicht wählen.
  const [searchParams] = useSearchParams();
  const deepLinkTaskId = searchParams.get('task');
  React.useEffect(() => {
    if (selectedId && !loading && !selected) setSelectedId(null);
  }, [loading, selected, selectedId]);

  const today = useToday();
  const width = useWindowWidth();
  const showNav = width >= NAV_MIN_WIDTH;
  const isMobile = width < MOBILE_MAX_WIDTH;
  const isTeam = view === 'team';
  const panelOverlay = isTeam || width < INLINE_PANEL_MIN_WIDTH;

  const ctx = React.useMemo(() => ({ userId: me.userId, today }), [me.userId, today]);
  const counts = React.useMemo(
    () =>
      Object.fromEntries(TASK_VIEWS.map((v) => [v.id, tasks.filter((t) => v.test(t, ctx)).length])) as Record<
        TaskViewId,
        number
      >,
    [ctx, tasks],
  );

  const listView = getTaskView(isTeam ? 'mine' : view);
  const groupBy: TaskGroupBy = groupByPrefs[listView.id] ?? 'faelligkeit';
  const setGroupBy = React.useCallback(
    (g: TaskGroupBy) =>
      setGroupByPrefs((prev) => {
        const next = { ...prev, [listView.id]: g };
        writeGroupByPrefs(next);
        return next;
      }),
    [listView.id],
  );
  const { lingering } = tasksApi;
  const visible = React.useMemo(
    () =>
      tasks
        // Gerade abgehakte bleiben kurz stehen, damit man sieht, was passiert ist.
        .filter((t) => listView.test(t, ctx) || lingering.has(t.id))
        .filter((t) => matchesTaskQuery(t, query, membersById)),
    [ctx, lingering, listView, membersById, query, tasks],
  );
  const groups = React.useMemo(
    () =>
      listView.id === 'done' ? groupDoneTasks(visible, today) : groupTasks(visible, groupBy, { today, members }),
    [groupBy, listView.id, members, today, visible],
  );

  React.useEffect(() => {
    if (!deepLinkTaskId || loading || !me.userId) return;
    const target = tasks.find((t) => t.id === deepLinkTaskId);
    const inCurrentView = target && !isTeam && listView.test(target, ctx);
    if (target) setSelectedId(target.id);
    const nextView: TaskViewId | 'team' = !target || inCurrentView ? view : target.done ? 'done' : 'all';
    // Query entfernen (replace), damit Zurück/Neuladen das Panel nicht erneut erzwingt.
    navigate(`${instancePrefix}/todos/${VIEW_SLUGS[nextView]}`, { replace: true });
  }, [ctx, deepLinkTaskId, instancePrefix, isTeam, listView, loading, me.userId, navigate, tasks, view]);

  const quickInputRef = React.useRef<HTMLInputElement>(null);
  const quickAdd = React.useCallback(
    async (title: string) => {
      const dueToday = listView.id === 'today' || listView.id === 'overdue' || listView.id === 'week';
      const created = await tasksApi.create({ title, ...(dueToday ? { due_date: today } : {}) });
      if (created && listView.id === 'done') selectView('all');
    },
    [listView.id, selectView, tasksApi, today],
  );

  const createDefaults = React.useMemo(
    () => ({
      assigned_user_id: me.userId,
      due_date: listView.id === 'today' || listView.id === 'overdue' || listView.id === 'week' ? today : null,
    }),
    [listView.id, me.userId, today],
  );
  /** Ausgefüllter Entwurf im Anlege-Panel — wird nicht still verworfen. */
  const [createDirty, setCreateDirty] = React.useState(false);
  const [pendingDiscard, setPendingDiscard] = React.useState<(() => void) | null>(null);
  const guardDraft = React.useCallback(
    (action: () => void) => {
      if (creating !== null && createDirty) setPendingDiscard(() => action);
      else action();
    },
    [createDirty, creating],
  );
  const closeCreate = React.useCallback(() => {
    setCreating(null);
    setCreateDirty(false);
  }, []);

  const openCreate = React.useCallback(() => {
    if (creating !== null) return; // schon offen — Entwurf behalten
    setSelectedId(null);
    setCreateDirty(false);
    setCreating(Date.now());
  }, [creating]);

  useEscapeClose(() => {
    // Erstes Esc in einem Feld verlässt nur das Feld (speichert per Blur), erst das zweite schließt.
    const active = document.activeElement;
    if (isTypingTarget(active)) {
      (active as HTMLElement).blur();
      return;
    }
    if (pendingDiscard) return;
    guardDraft(() => {
      setSelectedId(null);
      closeCreate();
    });
  }, Boolean(selectedId) || creating !== null);

  React.useEffect(() => {
    if (!canEdit) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'n' && e.key !== 'N') return;
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      if (document.querySelector('[data-app-select-list]')) return;
      e.preventDefault();
      // Team-Übersicht und „Überfällig" haben keine Schnell-Eingabe — dort öffnet N das Anlege-Panel.
      if (isTeam || !quickInputRef.current) openCreate();
      else quickInputRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canEdit, isTeam, openCreate]);

  const onSelect = React.useCallback(
    (t: Task) =>
      guardDraft(() => {
        closeCreate();
        setSelectedId(t.id);
      }),
    [closeCreate, guardDraft],
  );
  const onToggle = React.useCallback((t: Task) => void tasksApi.toggleDone(t.id), [tasksApi]);

  // Zurück-Geste schließt das Panel — nicht bei Direktlinks, die von woanders kommen.
  usePanelHistoryEntry(Boolean(selected) || creating !== null, isMobile && !deepLinkTaskId, () =>
    guardDraft(() => {
      setSelectedId(null);
      closeCreate();
    }),
  );

  // Schmale Fenster: Ansicht und „Neue Aufgabe" wandern in die Kopfzeile, mobil als Chips.
  const viewChips = isMobile ? (
    <TaskViewChips active={view} counts={counts} isAdmin={showTeam} showAllView={canViewAll} onSelect={selectView} />
  ) : null;
  const viewSelect =
    showNav || isMobile ? null : (
      <>
        <TaskViewSelect active={view} isAdmin={showTeam} showAllView={canViewAll} onSelect={selectView} />
        {canEdit && <NewTaskButton onClick={openCreate} className="!h-[30px] !px-2.5 !text-xs" />}
      </>
    );

  return (
    <div className="flex h-screen overflow-hidden bg-app-bg font-sans">
      <AppSidebar active="aufgaben" />

      {/* iOS Safari zoomt beim Fokus in Felder unter 16px — mobil daher 16px Schrift in allen Eingaben */}
      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-app-surface pb-16 md:pb-0 max-md:[&_input]:text-base max-md:[&_textarea]:text-base">
        <AppTopBar title="To-dos" />

        {tasksApi.error && (
          <div className="mx-4 mt-2 flex shrink-0 items-center justify-between rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <span>{tasksApi.error}</span>
            <button type="button" onClick={() => void tasksApi.reload()} className="text-red-500 hover:underline">
              Erneut versuchen
            </button>
          </div>
        )}

        <div className="relative flex min-h-0 flex-1">
          {showNav && (
            <TaskViewNav
              active={view}
              counts={counts}
              isAdmin={showTeam}
              showAllView={canViewAll}
              canEdit={canEdit}
              onSelect={selectView}
              onCreate={openCreate}
            />
          )}

          {isTeam ? (
            <TaskTeamOverview
              tasks={tasks}
              loading={loading}
              members={members}
              membersById={membersById}
              today={today}
              selectedId={selectedId}
              instancePrefix={instancePrefix}
              canOpenProjects={canOpenProjects}
              // Mobil hat die Team-Übersicht keine Suche — ein Suchtext aus der Liste soll nicht unsichtbar filtern.
              query={isMobile ? '' : query}
              onQuery={setQuery}
              viewSelect={viewChips ? <div className="flex w-full flex-col">{viewChips}</div> : viewSelect}
              onCreate={canEdit && isMobile ? openCreate : undefined}
              onSelect={onSelect}
            />
          ) : (
            <TaskList
              title={listView.label}
              subtitle={`${listView.sub(ctx)} · ${countLabel(visible.length)}`}
              groups={groups}
              loading={loading}
              failed={Boolean(tasksApi.error)}
              membersById={membersById}
              today={today}
              selectedId={selectedId}
              canEdit={canEdit}
              canEditTask={canEditTask}
              showQuickAdd={listView.id !== 'overdue'}
              showGroupBy={listView.id !== 'done'}
              emptyTitle={listView.id === 'done' ? 'Noch nichts erledigt' : 'Alles erledigt'}
              emptyText={listView.id === 'done' ? 'Erledigte To-dos erscheinen hier.' : 'Keine To-dos in dieser Ansicht.'}
              groupBy={groupBy}
              onGroupBy={setGroupBy}
              query={query}
              onQuery={setQuery}
              viewSelect={viewSelect}
              viewChips={viewChips}
              onCreate={canEdit && isMobile ? openCreate : undefined}
              quickInputRef={quickInputRef}
              onQuickAdd={(title) => void quickAdd(title)}
              onSelect={onSelect}
              onToggle={onToggle}
              keyboardEnabled={creating === null && !pendingDiscard}
              footer={
                listView.id === 'done' && tasksApi.hasMoreDone && !loading ? (
                  <div className="flex justify-center pt-2">
                    <button
                      type="button"
                      onClick={() => void tasksApi.loadOlderDone()}
                      disabled={tasksApi.loadingOlder}
                      className={`${APP_BTN_OUTLINE_CLASS} !h-8 text-xs`}
                    >
                      {tasksApi.loadingOlder ? 'Wird geladen…' : 'Ältere erledigte laden'}
                    </button>
                  </div>
                ) : null
              }
            />
          )}

          {creating !== null && canEdit && (
            <TaskCreatePanel
              key={creating}
              defaults={createDefaults}
              members={members}
              leadOptions={leadOptions}
              instancePrefix={instancePrefix}
              canOpenProjects={canOpenProjects}
              overlay={panelOverlay}
              tasksApi={tasksApi}
              onCreated={(t) => {
                closeCreate();
                setSelectedId(t.id);
              }}
              onDirtyChange={setCreateDirty}
              onClose={() => guardDraft(closeCreate)}
            />
          )}

          {selected && creating === null && (
            <TaskDetailPanel
              task={selected}
              today={today}
              members={members}
              membersById={membersById}
              leadOptions={leadOptions}
              instancePrefix={instancePrefix}
              canEdit={canEditTask(selected)}
              canOpenProjects={canOpenProjects}
              overlay={panelOverlay}
              tasksApi={tasksApi}
              onClose={() => setSelectedId(null)}
            />
          )}
        </div>
      </main>

      <AppToastViewport toasts={toasts} onDismiss={dismissToast} />

      <AppConfirmDialog
        open={pendingDiscard !== null}
        onClose={() => setPendingDiscard(null)}
        title="Entwurf verwerfen?"
        description="Das neue To-do ist noch nicht angelegt. Deine Eingaben gehen verloren."
        confirmLabel="Verwerfen"
        cancelLabel="Weiter bearbeiten"
        destructive
        onConfirm={() => {
          const action = pendingDiscard;
          setPendingDiscard(null);
          closeCreate();
          action?.();
        }}
      />
    </div>
  );
}
